import { LRUCache } from 'lru-cache';
import { TileAction, TileActionResponseMap, TileMessage, TileResponse, TileResult } from './tileWorker';


/**
 * Manages tile caching and retrieval, using an in-memory cache and a Web Worker for network/DB tasks.
 */
export class TileManager {
    private cache: LRUCache<string, ImageBitmap>;
    private worker: Worker;

    constructor(cacheSize: number = 100) {
        this.cache = new LRUCache({ max: cacheSize });
        this.worker = new Worker(new URL('./tileWorker.ts', import.meta.url), { type: "module" });
    }

    /**
     * Sends a message to the worker and waits for a response that matches the expected result.
     * 
     * @param message - The message to send to the worker, with its action field specifying the type.
     * @param expectedResult - The expected result identifier (TileResponse.result field) for the response.
     * @returns A Promise that resolves with the corresponding response type from TileActionResponseMap.
     */
    private async sendWorkerMessage<A extends TileAction>(
        message: Extract<TileMessage, { action: A }>,
        expectedResult: TileActionResponseMap[A]["result"]
    ): Promise<TileActionResponseMap[A]> {
        return new Promise((resolve, reject) => {
            const handler = (event: MessageEvent) => {
                const data = event.data as TileResponse;
                if (
                    data.result === expectedResult &&
                    (!('id' in message) || ('id' in data && data.id === message.id)) // If message has no id, skip id check; otherwise, ensure ids match
                ) {
                    this.worker.removeEventListener('message', handler);
                    resolve(data as TileActionResponseMap[A]);
                } else if (data.result === TileResult.Error) {
                    this.worker.removeEventListener('message', handler);
                    reject(new Error(data.error || 'Unknown worker error'));
                }
            };
            this.worker.addEventListener('message', handler);
            this.worker.postMessage(message);
        });
    }

    /**
     * Fetches a tile from the network and stores it
     * @param id Tile id
     * @param url URL to fetch the tile from
     * @returns Tile as an ImageBitmap
     */
    async fetchTileFromNetwork(id: string, url: string): Promise<ImageBitmap> {
        const { tileBlob } = await this.sendWorkerMessage(
            { action: TileAction.FetchTile, id, url },
            TileResult.TileFetched
        );
        const image = await createImageBitmap(tileBlob);
        await this.storeTile(id, tileBlob, image);
        return image;
    }

    /**
     * Stores a tile in the IndexedDB (as Blob) and in the cache (as ImageBitmap)
     * @param id Tile id.
     * @param tileBlob Tile data as a Blob.
     * @param image Decoded tile as an ImageBitmap.
     */
    async storeTile(id: string, tileBlob: Blob, image: ImageBitmap): Promise<void> {
        this.cache.set(id, image);
        await this.sendWorkerMessage(
            { action: TileAction.StoreTile, id, tileBlob },
            TileResult.TileStored
        );
    }

    /**
     * Retrieves a tile from cache, IndexedDB, or network (in that order).
     * @param id Tile identifier.
     * @param url URL for network fetch if needed.
     * @param forceFetch If true, skips cache and DB.
     * @returns Tile as an ImageBitmap.
     */
    async retrieveTile(id: string, url: string, forceFetch: boolean = false): Promise<ImageBitmap> {
        if (!forceFetch && this.cache.has(id)) return this.cache.get(id)!;

        if (!forceFetch) {
            const { tileBlob } = await this.sendWorkerMessage(
                { action: TileAction.RetrieveTile, id },
                TileResult.TileRetrieved
            );
            if (tileBlob) {
                const image = await createImageBitmap(tileBlob);
                this.cache.set(id, image);
                return image;
            }
        }

        return this.fetchTileFromNetwork(id, url);
    }

    clearCache(): void {
        this.cache.clear();
    }

    /** Clears both cache and IndexedDB storage. */
    async clearTiles(): Promise<void> {
        this.cache.clear();
        await this.sendWorkerMessage(
            { action: TileAction.ClearTiles },
            TileResult.TilesCleared
        );
    }

    terminateWorker(): void {
        this.worker.terminate();
    }
}